import { Avatar } from '@components/avatar';
import { IconCopy } from '@components/icons/copy';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { DataItemType } from '@components/table/types';
import { Text } from '@components/text';
import { useClipboard } from '@hooks/use-clipboard';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { abbreviate, formatPhoneNumber } from '@utils/strings';

import { AttributeType, TravellerAttributeTemplate } from 'api/attributes/requests';
import { User } from 'api/users/requests';

export const TableUserCell = <T extends DataItemType>({
  row: traveller,
  attribute,
  users,
}: {
  row: T;
  attribute: AttributeType | TravellerAttributeTemplate;
  users: User[];
}) => {
  const { t } = useScopedTranslate(locale);
  const { onCopy } = useClipboard();
  const attributeValue =
    traveller[`attribute-${attribute.shortId}`] ||
    traveller[`journey-attribute-${attribute.shortId}`];

  if (!attributeValue || !('shortId' in attributeValue)) {
    return null;
  }

  const user = users.find((user) => user.shortId === attributeValue.shortId);
  if (!user) return null;

  return (
    <Menu
      placement="bottom"
      buttonProps={{
        iconLeft: <Avatar name={user.name} src={user.picture} color="purple" />,
        size: 'sm',
        variant: 'secondary',
        text: abbreviate(user.name),
      }}
    >
      <Stack orientation="horizontal" align="center" space={2} style={{ padding: pxToRem(8) }}>
        <Avatar size="lg" name={user.name} src={user.picture} color="purple" />
        <div>
          <Text font="semibold14" text={user.name} />
          <Text font="regular14" text={user.email} color="textSecondary" />
          {user.phone && (
            <Text font="regular14" text={formatPhoneNumber(user.phone)} color="textSecondary" />
          )}
        </div>
      </Stack>
      <Menu.Item
        icon={<IconCopy />}
        label={t('copyEmail')}
        onSelect={() => {
          onCopy(user.email);
        }}
      />
      {user.phone && (
        <Menu.Item
          icon={<IconCopy />}
          label={t('copyPhone')}
          onSelect={() => {
            onCopy(user.phone!);
          }}
        />
      )}
    </Menu>
  );
};

const locale = {
  en: {
    copyPhone: 'Copy phone',
    copyEmail: 'Copy email',
  },
};
